import { dataWriter } from "@/functions/data";
import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";

const drawCountTarget = 500;

const SignWritingField = ({ onFinish }: { onFinish?: () => void }) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [width, setWidth] = useState(800); // Вынесено наверх
  const [height, setHeight] = useState(600); // Вынесено наверх
  const [drawCounter, updateDrawCounter] = useState(0);
  const [isDataSaved, setIsSaved] = useState(false);
  const [isPending, Pending] = useState(false);
  const drawCounterRef = useRef(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    ctx.strokeStyle = "blue";
    ctx.lineWidth = 4;
    ctx.lineCap = "round";
  }, []); // Инициализация контекста canvas

  // Обработка изменения размеров окна
  useEffect(() => {
    const canvasSizeSetup = () => {
      const startWidth = Math.min(window.innerWidth * 0.9, 800);
      const startHeight = Math.min(window.innerHeight * 0.4, 600);
      setWidth(startWidth);
      setHeight(startHeight);
    };

    canvasSizeSetup();
    window.addEventListener("resize", canvasSizeSetup);
    return () => {
      window.removeEventListener("resize", canvasSizeSetup);
    };
  }, []);

  // Обновление размеров canvas при изменении width/height
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    canvas.width = width;
    canvas.height = height;
    // Можно добавить перерисовку, если нужно
  }, [width, height]);

  // Обработка рисования
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const startDrawing = (e: MouseEvent | TouchEvent) => {
      setIsDrawing(true);
      updateDrawCounter(0);
      ctx.beginPath();
      const { offsetX, offsetY } = getCoords(e, canvas);
      ctx.moveTo(offsetX, offsetY);
    };

    const draw = (e: MouseEvent | TouchEvent) => {
      if (!isDrawing || drawCounter > drawCountTarget || isDataSaved) return;
      drawCounterRef.current = drawCounterRef.current + 1;
      updateDrawCounter((c) => {
        const next = c + 1;
        return next;
      });
      const { offsetX, offsetY } = getCoords(e, canvas);
      ctx.lineTo(offsetX, offsetY);
      ctx.stroke();
    };

    const stopDrawing = async () => {
      if (isDataSaved || isPending) return;
      setIsDrawing(false);
      Pending(true);
    
      const canvas = canvasRef.current;
      if (!canvas) return;
    
      const ctx = canvas.getContext("2d");
      if (!ctx) return;
    
      if (drawCounterRef.current < drawCountTarget) {
        toast("Недостаточно данных, начните заново"); 
        drawCounterRef.current = 0;
        updateDrawCounter(0);
        ctx.clearRect(0, 0, canvas.width, canvas.height); // Очистка
        Pending(false);
        return;    
      }
    
      try {
        const blob = await new Promise<Blob>((resolve) =>
          canvas.toBlob((b) => b && resolve(b), "image/png")
        );
    
        const arrayBuffer = await blob.arrayBuffer();
        const uint8Array = new Uint8Array(arrayBuffer);
        const base64String = canvas.toDataURL("image/png");
        dataWriter.writeImageDataReady(base64String);
        toast("Образец сохранен, переходим на следующий этап");
    
        drawCounterRef.current = 0;
        updateDrawCounter(0);
        setIsSaved(true);
        ctx.beginPath();
        if (onFinish) onFinish();
      } catch (err) {
        console.error("Ошибка при сохранении изображения:", err);
        toast("Ошибка при сохранении изображения");
      } finally {
        Pending(false);
        ctx.clearRect(0, 0, canvas.width, canvas.height); // Очистка
      }
    };

    canvas.addEventListener("mousedown", startDrawing);
    canvas.addEventListener("mousemove", draw);
    canvas.addEventListener("mouseup", stopDrawing);
    canvas.addEventListener("mouseleave", stopDrawing);

    canvas.addEventListener("touchstart", startDrawing);
    canvas.addEventListener("touchmove", draw);
    canvas.addEventListener("touchend", stopDrawing);

    return () => {
      canvas.removeEventListener("mousedown", startDrawing);
      canvas.removeEventListener("mousemove", draw);
      canvas.removeEventListener("mouseup", stopDrawing);
      canvas.removeEventListener("mouseleave", stopDrawing);

      canvas.removeEventListener("touchstart", startDrawing);
      canvas.removeEventListener("touchmove", draw);
      canvas.removeEventListener("touchend", stopDrawing);
    };
  }, [isDrawing]);

  // Вспомогательная функция для получения координат
  const getCoords = (e: MouseEvent | TouchEvent, canvas: HTMLCanvasElement) => {
    if (e instanceof TouchEvent && e.touches.length > 0) {
      const rect = canvas.getBoundingClientRect();
      return {
        offsetX: e.touches[0].clientX - rect.left,
        offsetY: e.touches[0].clientY - rect.top,
      };
    }
    if (e instanceof MouseEvent) {
      return { offsetX: e.offsetX, offsetY: e.offsetY };
    }
    return { offsetX: 0, offsetY: 0 };
  };

  return (
    <>
      <canvas ref={canvasRef} style={{ border: "1px solid black" }} />
      <div>{`${drawCounter} / ${drawCountTarget}`}</div>
    </>
  );
};

export default SignWritingField;
